import { styled } from "goober";
import {
  type DisplayableSpace,
  useSpaceDisplayName,
} from "../hooks/useSpaceDisplayName";
import { playClick } from "../lib/sounds";
import { spaceIcon } from "../lib/spaceIcon";
import { Button } from "./Button";
import { Dropdown } from "./Dropdown";
import { Icon } from "./Icon";
import { RedactedScopeRow } from "./RedactedScopeRow";
import { Text } from "./Text";
import { navigate, routes } from "../lib/urls";

const SpaceList = styled("div")`
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  overflow: hidden;
`;

const SpaceRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: inherit;
  cursor: pointer;
  &:not(:last-child) {
    border-bottom: 0.5px solid var(--border-alpha);
  }
  &:hover {
    background: var(--alpha4);
  }
  & .row-actions {
    margin-left: auto;
    opacity: 0;
  }
  &:hover .row-actions {
    opacity: 1;
  }
`;

const SpaceEmptyRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
`;

const SpaceIconContainer = styled("div")`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--alpha4);
  color: var(--text-secondary);
`;

const SpaceInfo = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export type UsableSpace = DisplayableSpace & { usableMemberCount: number };

export function UsableSpacesList({
  spaces,
  hiddenCount,
  resourceLabel,
  removableIds,
  onRemove,
}: {
  spaces: readonly UsableSpace[];
  hiddenCount: number;
  resourceLabel: string;
  removableIds?: ReadonlySet<string>;
  onRemove?: (spaceId: string) => void;
}) {
  const displaySpaceName = useSpaceDisplayName();

  if (spaces.length === 0 && hiddenCount === 0) {
    return (
      <SpaceList>
        <SpaceEmptyRow>
          <SpaceIconContainer>
            <Icon name="circle-slash" size={16} />
          </SpaceIconContainer>
          <SpaceInfo>
            <Text variant="text-regular-md" color="var(--text-primary)">
              No spaces yet
            </Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Spaces show up when every group with space access can also use
              this {resourceLabel}.
            </Text>
          </SpaceInfo>
        </SpaceEmptyRow>
      </SpaceList>
    );
  }

  return (
    <SpaceList>
      {spaces.map((space) => {
        const principalLabel =
          space.usableMemberCount === 1
            ? `1 member can use this ${resourceLabel}`
            : `${space.usableMemberCount} members can use this ${resourceLabel}`;
        return (
          <SpaceRow
            key={space.id}
            onMouseDown={() => playClick()}
            onClick={() => navigate(routes.modal.space, { id: space.id })}
          >
            <SpaceIconContainer>
              <Icon name={spaceIcon(space.mode)} size={16} />
            </SpaceIconContainer>
            <SpaceInfo>
              <Text
                variant="text-regular-md"
                color="var(--text-primary)"
                ellipsis
              >
                {displaySpaceName(space)}
              </Text>
              <Text variant="text-small" color="var(--text-secondary)">
                {principalLabel}
              </Text>
            </SpaceInfo>
            {onRemove && removableIds?.has(space.id) && (
              <div
                className="row-actions"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <Dropdown
                  opener={<Button variant="secondary" icon="more" />}
                  align="right"
                  minWidth={180}
                  sections={[
                    {
                      items: [
                        {
                          type: "button" as const,
                          left: <Icon name="trash" size={16} />,
                          label: "Remove access",
                          onClick: () => onRemove(space.id),
                        },
                      ],
                    },
                  ]}
                />
              </div>
            )}
          </SpaceRow>
        );
      })}
      <RedactedScopeRow count={hiddenCount} scopeKind="space" />
    </SpaceList>
  );
}
